"use client";

import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function PixelForceCommand() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 100);
    return () => clearTimeout(t);
  }, []);
  const headlineLines = ["The game demands", "absolute precision."];
  return <section data-section-id="3523" id="pixel-force-command" className="relative w-full h-screen min-h-[90vh] overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 w-full h-[120%] will-change-transform" style={{
      transform: `translateY(${scrollY * 0.3}px)`
    }}>
        <video ref={videoRef} autoPlay loop muted playsInline className="w-full h-full object-cover" poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_arena_hero.png">
          <source src="/img/esports_action_loop.mp4" type="video/mp4" />
        </video>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_arena_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_arena_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_arena_hero.png-wvc-sizes" alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover -z-10" />
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "linear-gradient(to bottom, hsla(240, 10%, 4%, 0.55) 0%, hsla(240, 10%, 4%, 0.25) 35%, hsla(240, 10%, 4%, 0.35) 60%, hsla(240, 10%, 4%, 0.75) 100%)"
    }} />

      {}
      <div className="hidden md:flex absolute left-4 lg:left-6 top-0 bottom-0 items-center pointer-events-none z-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-primary/70 font-mono" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          <span>EST. 2024 — 0.0000° N, 0.0000° E — NEURAL GRID</span>
        </div>
      </div>

      {}
      <div className="relative z-10 h-full w-full">
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 flex flex-col justify-center items-center px-6 sm:px-12 lg:px-20 pb-12">
          <div className="max-w-7xl w-full text-center">
            {}
            <div className={`mb-6 sm:mb-8 transition-all duration-1000 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="inline-block text-[10px] sm:text-xs tracking-[0.32em] uppercase text-primary/85 font-mono border-x border-primary/40 px-4">
                EST. COMMAND — ELITE ESPORTS GUILD
              </span>
            </div>

            {}
            <h1 className="font-default font-bold text-foreground leading-[0.95] tracking-tight text-[2.75rem] sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl mb-8 sm:mb-10">
              {headlineLines.map((line, i) => <span key={i} data-index={i} className="block overflow-hidden" style={{
              paddingBottom: "0.05em"
            }}>
                  <span className="block transition-all ease-out" style={{
                transitionDuration: "1200ms",
                transitionDelay: `${200 + i * 200}ms`,
                transform: revealed ? "translateY(0)" : "translateY(110%)",
                opacity: revealed ? 1 : 0
              }}>
                    {i === 0 ? <em className="italic font-bold text-primary">The game emands</em> : line}
                  </span>
                </span>)}
            </h1>

            {}
            <p className={`max-w-[600px] mx-auto text-sm sm:text-base text-foreground/80 leading-relaxed mb-8 sm:mb-10 transition-all duration-1000 delay-[1000ms] ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              PIXEL FORCE is a global competitive organization dedicated to the 
              future of esports — a digital command center where pro-players, 
              analysts, and the community converge to dominate the leaderboard.
            </p>

            {}
            <div className={`flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-5 transition-all duration-1000 delay-[1200ms] ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Link to="/TheNexusPage">
                <Button size="lg" className="h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-[0.2em] uppercase w-full sm:w-auto rounded-md">
                  Join the Nexus
                </Button>
              </Link>
              <Link to="/TheGridPage">
                <Button size="lg" variant="ghost" className="h-12 px-6 text-primary border border-primary hover:bg-primary/10 hover:text-primary font-mono text-xs tracking-[0.2em] uppercase w-full sm:w-auto group rounded-md">
                  <Play className="w-3.5 h-3.5 mr-1 fill-current" />
                  View Highlights
                  <span className="ml-1 transition-transform group-hover:translate-x-1">
                    →
                  </span>
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {}
        <div className="hidden md:flex absolute right-6 lg:right-10 bottom-10 lg:bottom-14 flex-col items-center gap-4 z-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-primary/70 font-mono" style={{
          writingMode: "vertical-rl"
        }}>SCROLL&nbsp;</div>
          <div className="relative h-16 w-px overflow-hidden bg-primary/15">
            <span className="absolute left-0 top-0 w-px bg-primary" style={{
            height: "40%",
            animation: "shoreScrollLine 2.4s ease-in-out infinite"
          }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shoreScrollLine {
          0% { transform: translateY(-100%); }
          50% { transform: translateY(60%); }
          100% { transform: translateY(160%); }
        }
      `}</style>
    </section>;
}